import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/react-hooks'
import { useDropzone } from 'react-dropzone'
import { toastr } from 'react-redux-toastr'
import { FileText, FolderKanban, LoaderCircle, Paperclip, RefreshCw, Trash2, Upload } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import * as Mutation from '../../../api/gql/mutation'
import { useUpload } from '../../../components/file-manager/hooks/useUpload'
import DocumentSelectorModal from '../../doc-control/components/DocumentSelectorModal'
import { useCostWriteQueue, WRITE, type WriteResult } from '../hooks/useCostWriteQueue'
import { describeCostControlError } from '../lib/errors'
import { attachmentVersionState } from '../lib/integrations'
import type { CostAttachment } from '../types'

interface Props {
  projectId: string
  attachments: CostAttachment[]
  canAdd: boolean
  canRemove: boolean
  /** Doc Control VIEW on the project: the document picker reads Doc Control folders. */
  canLinkDocuments: boolean
  /** Adds one attachment to the record the card belongs to; resolves `{ ok: false }` when the server refused it. */
  onAdd: (source: { fileId: string } | { docRevisionId: string }) => Promise<WriteResult>
  onRemove: (attachmentId: string) => Promise<WriteResult>
}

const RecordAttachmentsCard: React.FC<Props> = ({ projectId, attachments, canAdd, canRemove, canLinkDocuments, onAdd, onRemove }) => {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [linking, setLinking] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [removingId, setRemovingId] = useState<string | null>(null)
  const [switchingId, setSwitchingId] = useState<string | null>(null)
  const { run } = useCostWriteQueue()
  const [switchToCurrent] = useMutation(Mutation.SWITCH_COST_ATTACHMENT_TO_CURRENT, WRITE)
  const [createFile] = useMutation(Mutation.CREATE_FILE, WRITE)
  const [setFileUploaded] = useMutation(Mutation.SET_FILE_UPLOADED, WRITE)
  const [deleteFile] = useMutation(Mutation.DELETE_FILE, WRITE)
  const [upload] = useUpload()

  /** Uploaded files that did not end up attached are removed again (never leave orphans in 'cost-control'). */
  const discard = async (ids: string[]) => {
    if (!ids.length) return
    try { await deleteFile({ variables: { ids } }) } catch (e) { console.warn('Failed to clean up cost attachment uploads', e) }
  }

  const uploadFiles = async (files: File[]) => {
    if (!files.length) return
    setUploading(true)
    const created: string[] = []
    try {
      const prepared: { file: File; id: string; name: string; signedUrl: string }[] = []
      for (const file of files) {
        const mimetype = file.type || 'application/octet-stream'
        const { data } = await createFile({ variables: { input: { filename: file.name, size: file.size, mimetype, encoding: 'utf8', directory: 'cost-control' } } })
        created.push(data.createFile.file.id)
        // The upload URL is signed for `mimetype`; a file without a type must be sent with that same type.
        const body = file.type ? file : new File([file], file.name, { type: mimetype })
        prepared.push({ file: body, id: data.createFile.file.id, name: file.name, signedUrl: data.createFile.uploadUrl })
      }
      const uploaded = await upload(prepared)
      if (!Array.isArray(uploaded)) {
        await discard(created)
        return
      }
      await setFileUploaded({ variables: { ids: uploaded } })
      for (const fileId of uploaded) {
        const res = await onAdd({ fileId })
        if (!res.ok) await discard([fileId])
      }
    } catch (err: any) {
      await discard(created)
      toastr.error(t('Common_error'), describeCostControlError(err?.message, t))
    } finally {
      setUploading(false)
    }
  }

  const onFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    e.target.value = ''
    uploadFiles(files)
  }

  // Files dropped anywhere on the card upload like the button; clicks stay with the buttons and links inside.
  const { getRootProps, isDragActive } = useDropzone({ onDrop: uploadFiles, noClick: true, noKeyboard: true, disabled: !canAdd || uploading })

  const onDocuments = async (documents: any[]) => {
    setLinking(true)
    try {
      for (const doc of documents) {
        const docRevisionId = doc.currentRevisionId ?? doc.currentRevision?.id
        if (docRevisionId) await onAdd({ docRevisionId })
      }
    } finally {
      setLinking(false)
    }
  }

  const remove = async (id: string) => {
    setRemovingId(id)
    await onRemove(id)
    setRemovingId(null)
  }

  // The mutation returns the attachment; Apollo updates it by id in every query showing it, so nothing is reloaded.
  const switchVersion = async (id: string) => {
    setSwitchingId(id)
    await run((o) => switchToCurrent(o), { id }, [])
    setSwitchingId(null)
  }

  const excludeDocumentIds = attachments.map((a) => a.documentId).filter((id): id is string => !!id)

  return (
    <Card {...getRootProps({ className: cn('border-0 shadow-sm', isDragActive && 'ring-2 ring-primary') })}>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
        <CardTitle className="text-base">{t('CostControl_attachments_title')}</CardTitle>
        {canAdd && (
          <div className="flex items-center gap-2">
            <input ref={inputRef} type="file" multiple className="hidden" onChange={onFiles} />
            <Button variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={uploading}>
              {uploading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {t('CostControl_attachments_upload')}
            </Button>
            {canLinkDocuments && (
              <Button variant="outline" size="sm" onClick={() => setPickerOpen(true)} disabled={linking}>
                {linking ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <FolderKanban className="h-4 w-4" />}
                {t('CostControl_attachments_from_doc_control')}
              </Button>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent>
        {attachments.length === 0 && <p className="text-sm text-muted-foreground">{t('CostControl_attachments_empty')}</p>}
        {canAdd && <p className={cn('text-xs', isDragActive ? 'text-primary' : 'text-muted-foreground', attachments.length > 0 && 'mt-2')}>{t('CostControl_attachments_drop_hint')}</p>}
        {attachments.length > 0 && (
          <ul className="space-y-2">
            {attachments.map((a) => {
              const Icon = a.source === 'DOC_REVISION' ? FileText : Paperclip
              const versionState = attachmentVersionState(a)
              return (
                <li key={a.id} className="flex items-center gap-2 rounded border px-2.5 py-2 text-sm">
                  <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  {a.url ? (
                    <a href={a.url} target="_blank" rel="noopener noreferrer" className="min-w-0 flex-1 truncate text-primary hover:underline" title={a.name}>{a.name}</a>
                  ) : (
                    <span className="min-w-0 flex-1 truncate text-muted-foreground" title={t('CostControl_attachment_no_access')}>{a.name}</span>
                  )}
                  {a.source === 'DOC_REVISION' && <Badge variant="secondary" className="whitespace-nowrap">{t('CostControl_attachment_doc_badge', { version: a.version ?? '' })}</Badge>}
                  {versionState === 'outdated' && (
                    <>
                      {a.currentUrl
                        ? <a href={a.currentUrl} target="_blank" rel="noopener noreferrer"><Badge variant="warning" className="whitespace-nowrap">{t('CostControl_attachment_current_badge', { version: a.currentVersion })}</Badge></a>
                        : <Badge variant="warning" className="whitespace-nowrap">{t('CostControl_attachment_current_badge', { version: a.currentVersion })}</Badge>}
                      {canRemove && !!a.currentUrl && (
                        <Button variant="outline" size="sm" className="h-7 shrink-0" onClick={() => switchVersion(a.id)} disabled={switchingId === a.id}>
                          {switchingId === a.id ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                          {t('CostControl_attachment_use_current', { version: a.currentVersion })}
                        </Button>
                      )}
                    </>
                  )}
                  {versionState === 'deleted' && <Badge variant="neutral" className="whitespace-nowrap">{t('CostControl_attachment_document_deleted')}</Badge>}
                  <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground">
                    {a.addedByName ?? '—'} · {new Date(a.createdAt).toLocaleDateString(t('date_locale') || 'cs-CZ')}
                  </span>
                  {canRemove && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={() => remove(a.id)}
                      disabled={removingId === a.id}
                      aria-label={t('CostControl_attachment_remove')}
                    >
                      {removingId === a.id ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    </Button>
                  )}
                </li>
              )
            })}
          </ul>
        )}
        {attachments.some((a) => a.source === 'DOC_REVISION' && !a.url) && <p className="mt-2 text-xs text-muted-foreground">{t('CostControl_attachment_no_access')}</p>}
      </CardContent>
      {canLinkDocuments && (
        <DocumentSelectorModal isOpen={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={onDocuments} excludeDocumentIds={excludeDocumentIds} projectId={projectId} />
      )}
    </Card>
  )
}
export default RecordAttachmentsCard
